import { useState, type ReactNode } from 'react';
import { stagger } from '@/lib/motion';
import { useBreakpoint } from '@/lib/useBreakpoint';
import { cn } from '@/lib/utils';
import { CopyButton } from '@/components/CopyButton';
import { Icon } from '@/components/Icon';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { stepNumber, type StepState } from './steps';
import { collapsedByDefault, markerTone, type MarkerTone } from './stepViewModel';

interface StepCardProps {
  state: StepState;
  /** The step "Next" points at: the one accent of the step list (ring, number, pill). */
  current: boolean;
  title: string;
  /** Why the step matters, one line. */
  summary: ReactNode;
  /** Phones, collapsed: what exists, in one line ("1 account · 1 slot"). */
  collapsedNote?: ReactNode;
  /** Replaces the status pill (e.g. "No access" when the role cannot read the step's data). */
  pill?: ReactNode;
  children?: ReactNode;
  /** Bottom bar: a note on the left (`footnote`), the step's actions on the right. */
  footnote?: ReactNode;
  footer?: ReactNode;
  index: number;
}

/** Step status as a pill: "Done", "Attention required", "Next", "To do", "Needs step N", "Checking". */
export function StepPill({ state, current }: { state: StepState; current: boolean }) {
  const base = 'gap-1.5 font-mono text-[11px] font-normal leading-4';
  if (state.status === 'done') return <Badge variant="outline" data-pill="done" className={cn(base, 'border-succeeded/30 bg-succeeded/10 text-succeeded')}><Dot className="bg-succeeded" />Done</Badge>;
  if (state.status === 'attention') return <Badge variant="outline" data-pill="attention" className={cn(base, 'border-awaiting/30 bg-awaiting/10 text-awaiting')}><Dot className="bg-awaiting" />Attention required</Badge>;
  if (state.status === 'blocked') return <Badge variant="outline" data-pill="blocked" className={cn(base, 'text-subtle')}><Icon name="lock" size={12} />Needs step {stepNumber(state.blockedBy ?? state.id)}</Badge>;
  if (state.status === 'checking') return <Badge variant="outline" data-pill="checking" className={cn(base, 'text-subtle')}>Checking…</Badge>;
  if (current) return <Badge variant="outline" data-pill="next" className={cn(base, 'border-primary/40 bg-primary/10 text-primary')}><Dot className="bg-primary" />Next</Badge>;
  return <Badge variant="outline" data-pill="todo" className={cn(base, 'text-muted-foreground')}>To do</Badge>;
}

const Dot = ({ className }: { className: string }) => <span aria-hidden className={cn('size-1.5 rounded-full', className)} />;

const MARKER_TONE_CLASS: Record<MarkerTone, string> = {
  done: 'border-succeeded/30 bg-succeeded/10 text-succeeded',
  attention: 'border-awaiting/40 bg-awaiting/10 text-awaiting',
  current: 'border-primary bg-primary text-primary-foreground',
  idle: 'border-border text-muted-foreground',
};

/** What a marker shows: a check when done, "!" when it needs attention, else the step number. */
export function MarkerGlyph({ tone, n, iconSize }: { tone: MarkerTone; n: number; iconSize: number }) {
  if (tone === 'done') return <Icon name="check" size={iconSize} />;
  return <>{tone === 'attention' ? '!' : n}</>;
}

/** The round marker: a check when done, "!" when it needs attention, else the step number (filled when current). */
function StepMarker({ state, current }: { state: StepState; current: boolean }) {
  const tone = markerTone(state.status, current);
  return (
    <span aria-hidden className={cn('flex size-7 shrink-0 items-center justify-center rounded-full border font-mono text-xs font-semibold', MARKER_TONE_CLASS[tone])}>
      <MarkerGlyph tone={tone} n={stepNumber(state.id)} iconSize={16} />
    </span>
  );
}

interface HeaderProps {
  state: StepState;
  current: boolean;
  title: string;
  pill: ReactNode;
  titleClass: string;
}

/** Phones: the whole header is the toggle (the heading holds it; phrasing content only inside both). */
function PhoneHeader({ state, current, title, pill, titleClass, open, collapsedNote }: HeaderProps & { open: boolean; collapsedNote: ReactNode }) {
  return (
    <h2 className="m-0">
      <CollapsibleTrigger asChild>
        <button type="button" data-testid={`gs-step-${state.id}-toggle`} className="flex min-h-11 w-full items-start gap-3 px-4 pb-3 pt-4 text-left outline-none focus-visible:bg-accent">
          <StepMarker state={state} current={current} />
          <span className="flex min-w-0 flex-1 flex-col gap-1">
            <span className="flex min-w-0 flex-wrap items-center justify-between gap-x-3 gap-y-1">
              <span className={titleClass}>{title}</span>
              {pill}
            </span>
            {!open && collapsedNote ? <span className="truncate text-[13px] font-normal text-muted-foreground">{collapsedNote}</span> : null}
          </span>
          <Icon name={open ? 'expand_less' : 'expand_more'} size={20} className="mt-0.5 shrink-0 text-subtle" />
        </button>
      </CollapsibleTrigger>
    </h2>
  );
}

/** From 641 px: marker, title and pill, always open. */
function WideHeader({ state, current, title, pill, titleClass }: HeaderProps) {
  return (
    <div className="flex items-start gap-3 px-5 pb-3 pt-5">
      <StepMarker state={state} current={current} />
      <div className="flex min-w-0 flex-1 flex-wrap items-center justify-between gap-x-3 gap-y-1">
        <h2 className={titleClass}>{title}</h2>
        {pill}
      </div>
    </div>
  );
}

/** Bottom bar: a note on the left (`footnote`), the step's actions on the right. */
function StepFooter({ footnote, footer }: { footnote: ReactNode; footer: ReactNode }) {
  if (!footer && !footnote) return null;
  return (
    <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 border-t px-4 py-3 sm:px-5">
      <div className="min-w-0 text-xs text-subtle">{footnote}</div>
      <div className="flex flex-wrap items-center justify-end gap-2">{footer}</div>
    </div>
  );
}

/**
 * One card per setup step: marker, title, status pill, why it matters, what exists (a compact table) and the step's
 * actions. On phones a done or waiting step collapses to one line and the current one stays open; from 641 px every
 * card is open (the stepper and the rail carry the overview).
 */
export function StepCard({ state, current, title, summary, collapsedNote, pill, children, footnote, footer, index }: StepCardProps) {
  const phone = useBreakpoint() === 'phone';
  // The default follows the step's status (it settles as the data arrives) until the person toggles the card.
  const [toggled, setOpen] = useState<boolean | null>(null);
  const open = toggled ?? !collapsedByDefault(state.status, current);
  const expanded = !phone || open;
  const blocked = state.status === 'blocked';
  const header = { state, current, title, pill: pill ?? <StepPill state={state} current={current} />, titleClass: cn('min-w-0 text-[15px] font-semibold tracking-tight', blocked ? 'text-muted-foreground' : 'text-foreground') };
  return (
    <Card
      id={`gs-step-${state.id}`}
      data-testid={`gs-step-${state.id}`}
      data-status={state.status}
      data-next={current || undefined}
      data-expanded={expanded}
      className={cn('enter scroll-mt-24 gap-0 overflow-hidden p-0', current && 'border-primary/50 ring-1 ring-primary/30', blocked && 'bg-card/70')}
      style={stagger(index + 2)}
    >
      <Collapsible open={expanded} onOpenChange={setOpen}>
        {phone ? <PhoneHeader {...header} open={open} collapsedNote={collapsedNote} /> : <WideHeader {...header} />}
        <CollapsibleContent>
          <div className={cn('flex min-w-0 flex-col gap-3 px-4 pb-4 sm:px-5 sm:pb-5', blocked && 'opacity-80')}>
            <p className="text-[13px] text-muted-foreground sm:-mt-1">{summary}</p>
            {children}
          </div>
          <StepFooter footnote={footnote} footer={footer} />
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
}

/** A shell command to copy as is: its own label, a copy button, and horizontal scroll instead of wrapping. */
export function CommandBlock({ label, command, testId, meta }: { label: string; command: string; testId?: string; meta?: ReactNode }) {
  return (
    <div data-testid={testId} className="min-w-0 overflow-hidden rounded-lg border bg-secondary/40">
      <div className="flex items-center justify-between gap-2 border-b px-3 py-1">
        <span className="truncate font-mono text-[11px] text-subtle">{label}</span>
        <span className="flex shrink-0 items-center gap-2">
          {meta ? <span className="font-mono text-[11px] text-subtle max-sm:hidden">{meta}</span> : null}
          <CopyButton text={command} size="icon-sm" label={`Copy: ${label}`} testId={testId ? `${testId}-copy` : undefined} />
        </span>
      </div>
      <pre className="overflow-x-auto px-3 py-2.5 font-mono text-xs leading-5 text-foreground">
        <code>{command}</code>
      </pre>
    </div>
  );
}

/** Ordered sub-steps inside a step body. */
export function Steps({ items }: { items: ReactNode[] }) {
  return (
    <ol className="flex flex-col gap-1.5">
      {items.map((item, i) => (
        <li key={i} className="flex gap-2">
          <span className="w-4 shrink-0 text-right font-mono text-[12px] text-subtle">{i + 1}.</span>
          <span className="min-w-0">{item}</span>
        </li>
      ))}
    </ol>
  );
}

/** One line of guidance inside a step: info, a warning to fix, or the reason a step waits. */
export function StepNote({ tone = 'info', icon, children, action, testId }: { tone?: 'info' | 'warning' | 'muted'; icon?: string; children: ReactNode; action?: ReactNode; testId?: string }) {
  return (
    <div
      data-testid={testId}
      className={cn(
        'flex min-w-0 items-start gap-2 rounded-lg border px-3 py-2 text-[13px]',
        tone === 'warning' && 'border-awaiting/30 bg-awaiting/10 text-foreground',
        tone === 'info' && 'bg-secondary/40 text-foreground',
        tone === 'muted' && 'border-dashed text-muted-foreground',
      )}
    >
      <Icon name={icon ?? (tone === 'warning' ? 'warning' : tone === 'muted' ? 'lock' : 'info')} size={16} className={cn('mt-0.5 shrink-0', tone === 'warning' ? 'text-awaiting' : 'text-subtle')} />
      <span className="min-w-0 flex-1">{children}</span>
      {action ? <span className="-my-1 shrink-0">{action}</span> : null}
    </div>
  );
}

/** Inline code in step prose. */
export const code = (text: string) => <code className="rounded bg-secondary px-1 py-0.5 font-mono text-[12px]">{text}</code>;
